/**
 * Сторінка адміністратора для управління містами
 * @component AdminCitiesPage
 * @file AdminCitiesPage.tsx
 * @path frontend/src/pages/AdminCitiesPage/AdminCitiesPage.tsx
 */

import React, { useState, useEffect } from 'react';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, LocationOn as LocationIcon, Save as SaveIcon } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';
import './AdminCitiesPage.css';

interface City {
  id: string;
  name_uk: string;
  name_en: string;
  region: string;
  latitude: number;
  longitude: number;
  is_popular: boolean;
  created_at: string;
  updated_at: string;
}

interface CityFormData {
  name_uk: string;
  name_en: string;
  region: string;
  latitude: number;
  longitude: number;
  is_popular: boolean;
}

const AdminCitiesPage: React.FC = () => {
  const { language } = useLanguage();
  const { addNotification } = useNotification();
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<CityFormData>({
    name_uk: '',
    name_en: '',
    region: '',
    latitude: 0,
    longitude: 0,
    is_popular: false
  });

  // Load cities
  const loadCities = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/v1/cities', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const citiesResponse = await response.json();
        // Combine popular and all cities, remove duplicates
        const allCities = [...citiesResponse.popular, ...citiesResponse.all];
        const uniqueCities = allCities.filter((city, index, self) =>
          index === self.findIndex(c => c.id === city.id)
        );
        setCities(uniqueCities);
      } else {
        throw new Error('Failed to load cities');
      }
    } catch (error) {
      console.error('Error loading cities:', error);
      addNotification(language === 'ua' ? 'Помилка при завантаженні міст' : 'Error loading cities', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('auth_token');
      const url = editingCity
        ? `/api/v1/admin/cities/${editingCity.id}`
        : '/api/v1/admin/cities';
      const method = editingCity ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        await loadCities();
        setShowForm(false);
        setEditingCity(null);
        resetForm();
        addNotification(
          language === 'ua'
            ? (editingCity ? 'Місто успішно оновлено' : 'Місто успішно додано')
            : (editingCity ? 'City updated successfully' : 'City added successfully'),
          'success'
        );
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save city');
      }
    } catch (error: any) {
      console.error('Error saving city:', error);
      addNotification(
        language === 'ua' ? `Помилка: ${error.message}` : `Error: ${error.message}`,
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit
  const handleEdit = (city: City) => {
    setEditingCity(city);
    setFormData({
      name_uk: city.name_uk,
      name_en: city.name_en || '',
      region: city.region || '',
      latitude: city.latitude,
      longitude: city.longitude,
      is_popular: city.is_popular
    });
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (cityId: string, cityName: string) => {
    if (!window.confirm(
      language === 'ua'
        ? `Ви впевнені, що хочете видалити місто "${cityName}"?`
        : `Are you sure you want to delete city "${cityName}"?`
    )) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/v1/admin/cities/${cityId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await loadCities();
        addNotification(
          language === 'ua' ? 'Місто успішно видалено' : 'City deleted successfully',
          'success'
        );
      } else {
        throw new Error('Failed to delete city');
      }
    } catch (error) {
      console.error('Error deleting city:', error);
      addNotification(language === 'ua' ? 'Помилка при видаленні міста' : 'Error deleting city', 'error');
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name_uk: '',
      name_en: '',
      region: '',
      latitude: 0,
      longitude: 0,
      is_popular: false
    });
  };

  // Cancel form
  const handleCancel = () => {
    setShowForm(false);
    setEditingCity(null);
    resetForm();
  };

  // Format coordinates
  const formatCoordinates = (lat: number, lng: number) => {
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  };

  // Load data on component mount
  useEffect(() => {
    loadCities();
  }, []);

  if (loading) {
    return (
      <div className="admin-cities-page">
        <div className="loading">
          {language === 'ua' ? 'Завантаження...' : 'Loading...'}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-cities-page">
      <div className="settings-card">
        <div className="page-header">
          <h1>
            <LocationIcon sx={{ fontSize: 28, opacity: 0.9 }} />
            {language === 'ua' ? 'Управління містами' : 'Cities Management'}
          </h1>
          <p className="page-subtitle">
            {language === 'ua'
              ? 'Переглядайте та редагуйте міста у системі'
              : 'View and edit cities in the system'
            }
          </p>
        </div>

        {/* Add City Button */}
        <div className="cities-actions">
          <button
            className="add-city-btn"
            onClick={() => setShowForm(true)}
            disabled={showForm}
          >
            <AddIcon sx={{ fontSize: 16 }} />
            <span>{language === 'ua' ? 'Додати місто' : 'Add City'}</span>
          </button>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="city-form-container">
            <h3>
              {editingCity
                ? (language === 'ua' ? 'Редагувати місто' : 'Edit City')
                : (language === 'ua' ? 'Додати нове місто' : 'Add New City')
              }
            </h3>
            <form onSubmit={handleSubmit} className="city-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name_uk">
                    {language === 'ua' ? 'Назва (українською)*' : 'Name (Ukrainian)*'}
                  </label>
                  <input
                    id="name_uk"
                    type="text"
                    value={formData.name_uk}
                    onChange={(e) => setFormData(prev => ({ ...prev, name_uk: e.target.value }))}
                    placeholder={language === 'ua' ? 'Введіть назву міста' : 'Enter city name'}
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="name_en">
                    {language === 'ua' ? 'Назва (англійською)' : 'Name (English)'}
                  </label>
                  <input
                    id="name_en"
                    type="text"
                    value={formData.name_en}
                    onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                    placeholder={language === 'ua' ? 'Введіть назву міста англійською' : 'Enter city name in English'}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="region">
                    {language === 'ua' ? 'Регіон' : 'Region'}
                  </label>
                  <input
                    id="region"
                    type="text"
                    value={formData.region}
                    onChange={(e) => setFormData(prev => ({ ...prev, region: e.target.value }))}
                    placeholder={language === 'ua' ? 'Введіть регіон' : 'Enter region'}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_popular}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_popular: e.target.checked }))}
                    />
                    {' '}
                    {language === 'ua' ? 'Популярне місто' : 'Popular city'}
                  </label>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="latitude">
                    {language === 'ua' ? 'Широта*' : 'Latitude*'}
                  </label>
                  <input
                    id="latitude"
                    type="number"
                    step="0.000001"
                    min="-90"
                    max="90"
                    value={formData.latitude}
                    onChange={(e) => setFormData(prev => ({ ...prev, latitude: parseFloat(e.target.value) || 0 }))}
                    placeholder="50.4501"
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="longitude">
                    {language === 'ua' ? 'Довгота*' : 'Longitude*'}
                  </label>
                  <input
                    id="longitude"
                    type="number"
                    step="0.000001"
                    min="-180"
                    max="180"
                    value={formData.longitude}
                    onChange={(e) => setFormData(prev => ({ ...prev, longitude: parseFloat(e.target.value) || 0 }))}
                    placeholder="30.5234"
                    required
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="cancel-btn"
                  disabled={isSubmitting}
                >
                  {language === 'ua' ? 'Скасувати' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="save-btn"
                >
                  <SaveIcon sx={{ fontSize: 16 }} />
                  <span>
                    {isSubmitting
                      ? (language === 'ua' ? 'Збереження...' : 'Saving...')
                      : (language === 'ua' ? 'Зберегти' : 'Save')
                    }
                  </span>
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Cities Table */}
        <div className="cities-section">
          {cities.length === 0 ? (
            <div className="no-cities">
              <LocationIcon sx={{ fontSize: 48, opacity: 0.5 }} />
              <p>
                {language === 'ua'
                  ? 'Немає міст у системі'
                  : 'No cities found in the system'
                }
              </p>
            </div>
          ) : (
            <div className="cities-table-container">
              <table className="cities-table">
                <thead>
                  <tr>
                    <th>{language === 'ua' ? 'Назва (UA)' : 'Name (UA)'}</th>
                    <th>{language === 'ua' ? 'Назва (EN)' : 'Name (EN)'}</th>
                    <th>{language === 'ua' ? 'Регіон' : 'Region'}</th>
                    <th>{language === 'ua' ? 'Координати' : 'Coordinates'}</th>
                    <th>{language === 'ua' ? 'Популярне' : 'Popular'}</th>
                    <th>{language === 'ua' ? 'Створено' : 'Created'}</th>
                    <th>{language === 'ua' ? 'Дії' : 'Actions'}</th>
                  </tr>
                </thead>
                <tbody>
                  {cities.map((city) => (
                    <tr key={city.id} className="city-row">
                      <td className="name-cell">{city.name_uk}</td>
                      <td className="name-cell">{city.name_en || '-'}</td>
                      <td className="region-cell">{city.region || '-'}</td>
                      <td className="coordinates-cell">
                        {formatCoordinates(city.latitude, city.longitude)}
                      </td>
                      <td className="popular-cell">
                        {city.is_popular ? (
                          <span className="popular-badge">
                            {language === 'ua' ? 'Так' : 'Yes'}
                          </span>
                        ) : (
                          <span className="not-popular-badge">
                            {language === 'ua' ? 'Ні' : 'No'}
                          </span>
                        )}
                      </td>
                      <td className="date-cell">
                        {new Date(city.created_at).toLocaleDateString(language === 'ua' ? 'uk-UA' : 'en-US')}
                      </td>
                      <td className="actions-cell">
                        <button
                          className="edit-btn"
                          onClick={() => handleEdit(city)}
                          title={language === 'ua' ? 'Редагувати' : 'Edit'}
                          disabled={showForm}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(city.id, city.name_uk)}
                          title={language === 'ua' ? 'Видалити' : 'Delete'}
                          disabled={true}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCitiesPage;
